import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Box, Typography, Button, Stack, Paper, Chip } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import FinancialTable from '../components/FinancialTable';
import { exportToExcel, exportToPrintWindow } from '../utils/exportUtils';
import {
  fetchFinance,
  fetchFinanceSummary,
  fetchFinanceForExport,
} from '../slices/financeSlice';
import PaymentIcon from '@mui/icons-material/Payment';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import AddIcon from '@mui/icons-material/Add';
import { motion } from 'framer-motion';

const MotionPaper = motion(Paper);

const today = new Date().toISOString().slice(0, 10);
const DEFAULT_FILTERS = {
  type: '', target_name: '', method: '', number: '', dateFrom: today, dateTo: today,
};

// Maps the table's filter field names to the payments API's query params.
const toApiFilters = (filterValues) => ({
  number: filterValues.number,
  type: filterValues.type,
  target_name: filterValues.target_name,
  payment_type: filterValues.method,
  date_from: filterValues.dateFrom,
  date_to: filterValues.dateTo,
});

const FinancialPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { filteredData: payments, count: rowCount, summary, status } = useSelector((state) => state.finance);

  const [filterValues, setFilterValues] = useState(() => {
    try {
      const saved = localStorage.getItem('financial_table_filters');
      return saved ? { ...DEFAULT_FILTERS, ...JSON.parse(saved) } : DEFAULT_FILTERS;
    } catch {
      return DEFAULT_FILTERS;
    }
  });
  const [debouncedFilters, setDebouncedFilters] = useState(filterValues);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });

  // Debounce free-text/date filters so we don't hit the API on every keystroke
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedFilters(filterValues), 400);
    return () => clearTimeout(handle);
  }, [filterValues]);

  // Reset to first page whenever a filter changes
  useEffect(() => {
    setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedFilters]);

  const apiFilters = useMemo(() => toApiFilters(debouncedFilters), [debouncedFilters]);

  const refetch = useCallback(() => {
    dispatch(fetchFinance({ ...apiFilters, page: paginationModel.page, pageSize: paginationModel.pageSize }));
  }, [dispatch, apiFilters, paginationModel]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    dispatch(fetchFinanceSummary(apiFilters));
  }, [dispatch, apiFilters]);

  const handleFilterChange = (field, value) => {
    setFilterValues((prev) => {
      const next = { ...prev, [field]: value };
      localStorage.setItem('financial_table_filters', JSON.stringify(next));
      return next;
    });
  };

  const handleDeleted = () => {
    refetch();
    dispatch(fetchFinanceSummary(apiFilters));
  };

  const getExportRows = async () => {
    return dispatch(fetchFinanceForExport(apiFilters)).unwrap();
  };

  const handleExportExcel = async () => {
    const columns = [
      { header: 'رقم السند', key: 'number' },
      { header: 'نوع الجهة', key: 'type' },
      { header: 'اسم المستفيد', key: 'recipient_name' },
      { header: 'المبلغ', key: 'amount' },
      { header: 'التاريخ', key: 'date' },
      { header: 'الوقت', key: 'time_display' },
      { header: 'طريقة الدفع', key: 'payment_type' },
      { header: 'ملاحظات', key: 'reason' },
    ];
    const data = (await getExportRows()).map(r => ({
      number: r.number,
      recipient_name: r.target_name,
      amount: r.amount,
      datetime_display: r.datetime_display || `${r.date || ''} ${r.time_display || ''}`.trim(),
      date: r.date,
      time_display: r.time_display,
      type: r.type,
      payment_type: r.payment_type,
      reason: r.reason,
    }));
    exportToExcel(data, columns, 'financial');
  };

  const handlePrint = async () => {
    const columns = [
      { header: 'رقم السند', key: 'number' },
      { header: 'التاريخ والوقت', key: 'datetime_display' },
      { header: 'نوع الجهة', key: 'type' },
      { header: 'اسم المستفيد', key: 'recipient_name' },
      { header: 'المبلغ', key: 'amount' },
      { header: 'ملاحظات', key: 'reason' },
    ];
    const data = (await getExportRows()).map(r => ({
      number: r.number,
      recipient_name: r.target_name,
      amount: r.amount,
      datetime_display: r.datetime_display || `${r.date || ''} ${r.time_display || ''}`.trim(),
      type: r.type,
      reason: r.reason,
    }));
    await exportToPrintWindow(data, columns, 'قائمة سندات الصرف', { summary: { amountKey: 'amount' } });
  };

  // Latest payment among the currently loaded page (rows are ordered by
  // number desc server-side, so this matches the true latest in the common
  // case of viewing the first page with no filters narrowing it out).
  const getLatestPaymentDate = () => {
    if (!payments || payments.length === 0) return '-';
    const sorted = [...payments].sort((a, b) => {
      const dateA = new Date(`${a.date} ${a.time || '00:00:00'}`);
      const dateB = new Date(`${b.date} ${b.time || '00:00:00'}`);
      return dateB - dateA;
    });
    return sorted[0]?.datetime_display || sorted[0]?.date || '-';
  };

  return (
    // 🎨 Design System Applied
    <Box
      sx={{
        direction: 'rtl',
        fontFamily: 'Cairo, Tajawal, sans-serif',
        p: { xs: 2, md: 3 },
        bgcolor: '#F8F9FF',
        minHeight: '100vh',
      }}
    >
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        {/* Header Section */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Stack useFlexGap direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                borderRadius: 2.5,
                width: 52,
                height: 52,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <PaymentIcon sx={{ color: '#fff', fontSize: 30 }} />
            </Box>
            <Box>
              <Typography
                variant="h5"
                fontWeight={800}
                color="#fff"
                sx={{ fontFamily: 'Cairo, Tajawal, sans-serif' }}
              >
                إدارة سندات الصرف
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255,255,255,0.8)',
                  mt: 0.25,
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                }}
              >
                إنشاء وإدارة وتصدير سندات الصرف المالية
              </Typography>
            </Box>
          </Stack>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/finance/create?type=سند صرف')}
            data-guide-id="guide-add-financial"
            sx={{
              bgcolor: '#fff',
              color: '#4338CA',
              px: 3,
              py: 1.1,
              borderRadius: 2.5,
              boxShadow: 'none',
              fontWeight: 700,
              textTransform: 'none',
              fontFamily: 'Cairo, Tajawal, sans-serif',
              '&:hover': { bgcolor: '#EEF2FF', boxShadow: 'none' },
            }}
          >
            إضافة سند صرف
          </Button>
        </MotionPaper>

        {/* Financial Summary Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
        >
          {/* Export Actions */}
          <MotionPaper
            component={motion.div}
            elevation={0}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.15, ease: 'easeOut' }}
            sx={{
              mb: 3,
              p: { xs: 2, sm: 2.5 },
              borderRadius: 3,
              bgcolor: '#fff',
              boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
              border: '1px solid #E5E7EB',
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                color: '#1E1B4B',
                fontFamily: 'Cairo, Tajawal, sans-serif',
              }}
            >
              تصدير القائمة
            </Typography>

            <Stack useFlexGap
              direction="row"
              spacing={1.5}
              sx={{ flexWrap: 'wrap', justifyContent: { xs: 'stretch', sm: 'flex-end' }, alignItems: 'center', gap: 1.5 }}
            >
              <Button
                variant="outlined"
                color="success"
                startIcon={<DownloadIcon />}
                onClick={handleExportExcel}
                sx={{
                  borderRadius: 2.5,
                  fontWeight: 700,
                  textTransform: 'none',
                  px: 2.5,
                  gap: 1,
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                  borderColor: '#16A34A',
                  color: '#16A34A',
                  '&:hover': { borderColor: '#16A34A', bgcolor: '#DCFCE7' },
                }}
              >
                تصدير Excel
                <Chip
                  label={`${summary.count || 0} سند`}
                  size="small"
                  sx={{
                    bgcolor: '#DCFCE7',
                    color: '#16A34A',
                    fontWeight: 700,
                    fontSize: '0.72rem',
                    height: 20,
                    fontFamily: 'Cairo, Tajawal, sans-serif',
                  }}
                />
              </Button>
              <Button
                variant="outlined"
                startIcon={<PictureAsPdfIcon />}
                onClick={handlePrint}
                sx={{
                  borderRadius: 2.5,
                  fontWeight: 700,
                  textTransform: 'none',
                  px: 2.5,
                  gap: 1,
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                  borderColor: '#4338CA',
                  color: '#4338CA',
                  '&:hover': { borderColor: '#4338CA', bgcolor: '#EEF2FF' },
                }}
              >
                طباعة
                <Chip
                  label={`${summary.count || 0} سند`}
                  size="small"
                  sx={{
                    bgcolor: '#EEF2FF',
                    color: '#4338CA',
                    fontWeight: 700,
                    fontSize: '0.72rem',
                    height: 20,
                    fontFamily: 'Cairo, Tajawal, sans-serif',
                  }}
                />
              </Button>
            </Stack>
          </MotionPaper>
          <FinancialTable
            data={payments}
            rowCount={rowCount}
            loading={status === 'loading'}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            filterValues={filterValues}
            onFilterChange={handleFilterChange}
            summary={summary}
            onDeleted={handleDeleted}
            showSummaryCards={true}
            getLatestPaymentDate={getLatestPaymentDate}
          />
        </motion.div>

        {/* تم حذف الأزرار المكررة من الأسفل */}

        {/* Table */}
        {/* تم نقل البطاقات الملونة إلى FinancialTable ليتم حسابها بعد الفلترة */}
      </Box>
    </Box>
  );
};

export default FinancialPage;
